/* FLX v4.4 — if this marker is missing in Shopify, the paste didn't save.
  FLX cart drawer — Dawn's cart-drawer.js with three FLX changes:
  1. The FLX header "Cart (n)" button (.flx-cart-btn) opens the drawer instead of going to /cart.
     On the /cart page itself it stays a normal link.
  2. While the drawer is open, chat widgets (Shopify Inbox, Tawk, Tidio, Crisp, Gorgias,
     Intercom, HubSpot, etc.) are hidden with inline styles — including any fixed-position
     iframe, which is how most chat bubbles are rendered — and restored on close.
  3. Each element is only defined once, so loading this file twice can't break the cart.
  Everything else is Dawn's original behaviour.
*/

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
      }

      setHeaderCartIconAccessibility() {
        const onCartPage = window.routes && window.location.pathname === window.routes.cart_url;
        const triggers = [
          document.querySelector('#cart-icon-bubble'),
          ...document.querySelectorAll('.flx-cart-btn'),
        ].filter(Boolean);

        triggers.forEach((cartLink) => {
          if (cartLink.dataset.flxCartBound === 'true') return;
          cartLink.dataset.flxCartBound = 'true';
          if (onCartPage) return;

          if (cartLink.tagName !== 'A') cartLink.setAttribute('role', 'button');
          cartLink.setAttribute('aria-haspopup', 'dialog');
          cartLink.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(cartLink);
          });
          cartLink.addEventListener('keydown', (event) => {
            if (event.code && event.code.toUpperCase() === 'SPACE') {
              event.preventDefault();
              this.open(cartLink);
            }
          });
        });
      }

      setChatHidden(hide) {
        try {
          const selectors = [
            '#shopify-chat',
            '#ShopifyChat',
            '[id^="shopify-chat"]',
            '#tidio-chat',
            '#tidio-chat-iframe',
            '[id^="tawk"]',
            '#tawkchat-container',
            '#crisp-chatbox',
            '.crisp-client',
            '#gorgias-chat-container',
            '#hubspot-messages-iframe-container',
            '#intercom-container',
            '.intercom-lightweight-app',
            '#chat-button',
            '[id*="chat-widget"]',
            '[class*="chat-widget"]',
          ].join(',');

          const nodes = new Set(document.querySelectorAll(selectors));
          const chatPattern = /chat|tawk|tidio|crisp|intercom|messenger|gorgias|zendesk|livechat|hubspot|inbox/i;

          // A chat bubble can only float above the drawer by being fixed-position
          // with a very high z-index. Sweep the top two levels of the page for
          // exactly that, plus any fixed iframe or chat-named container.
          document.querySelectorAll('body > *, body > * > *').forEach((el) => {
            if (this.contains(el) || el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'CART-DRAWER') return;
            const cs = getComputedStyle(el);
            if (cs.position !== 'fixed') return;
            const z = parseInt(cs.zIndex, 10);
            const idClass = (el.id || '') + ' ' + (typeof el.className === 'string' ? el.className : '');
            if (el.tagName === 'IFRAME' || (!isNaN(z) && z >= 500000) || chatPattern.test(idClass)) {
              nodes.add(el);
            }
          });

          // And any iframe, anywhere, that identifies as a chat widget.
          document.querySelectorAll('iframe').forEach((frame) => {
            if (this.contains(frame)) return;
            const label = (frame.src || '') + ' ' + (frame.title || '') + ' ' + (frame.id || '') + ' ' + (frame.name || '');
            if (chatPattern.test(label) || getComputedStyle(frame).position === 'fixed') nodes.add(frame);
          });

          // Shopify Inbox's current widget renders inside a shadow DOM, which the
          // queries above can't see into. Hiding the HOST element hides everything
          // inside it — so hide body-level custom elements that carry a shadow root
          // or a chat-like tag name.
          document.querySelectorAll('body > *').forEach((el) => {
            if (this.contains(el) || el.tagName === 'CART-DRAWER') return;
            const isCustomEl = el.tagName.includes('-');
            if ((isCustomEl && el.shadowRoot) || chatPattern.test(el.tagName)) nodes.add(el);
          });

          nodes.forEach((node) => {
            if (this.contains(node)) return;
            if (hide) {
              if (node.dataset.flxChatHidden === 'true') return;
              node.dataset.flxChatHidden = 'true';
              node.dataset.flxPrevVisibility = node.style.visibility || '';
              node.style.setProperty('visibility', 'hidden', 'important');
              node.style.setProperty('pointer-events', 'none', 'important');
            }
          });

          if (!hide) {
            document.querySelectorAll('[data-flx-chat-hidden="true"]').forEach((node) => {
              node.style.visibility = node.dataset.flxPrevVisibility || '';
              node.style.removeProperty('pointer-events');
              delete node.dataset.flxChatHidden;
              delete node.dataset.flxPrevVisibility;
            });
          }
        } catch (e) {
          /* chat hiding is cosmetic — never let it break the cart */
        }
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
          this.classList.add('animate', 'active');
          this.setChatHidden(true);
          // Chat widgets load late and can re-show themselves — keep sweeping while open.
          if (this._chatTimer) clearInterval(this._chatTimer);
          this._chatTimer = setInterval(() => this.setChatHidden(true), 400);
        });

        this.addEventListener(
          'transitionend',
          () => {
            const containerToTrapFocusOn = this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true }
        );

        document.body.classList.add('overflow-hidden', 'flx-cart-open');
      }

      close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden', 'flx-cart-open');
        if (this._chatTimer) {
          clearInterval(this._chatTimer);
          this._chatTimer = null;
        }
        this.setChatHidden(false);
      }

      setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute('role', 'button');
        cartDrawerNote.setAttribute('aria-expanded', 'false');

        if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
          cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
        }

        cartDrawerNote.addEventListener('click', (event) => {
          event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
        });

        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
      }

      renderContents(parsedState) {
        this.querySelector('.drawer__inner').classList.contains('is-empty') &&
          this.querySelector('.drawer__inner').classList.remove('is-empty');
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (!sectionElement) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        setTimeout(() => {
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.open();
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer',
            selector: '#CartDrawer',
          },
          {
            id: 'cart-icon-bubble',
          },
        ];
      }

      getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }
  );
}
